import {
	Component,
	OnInit,
	ViewChildren,
	QueryList,
	AfterViewInit,
	Input,
	Output,
	EventEmitter,
	OnChanges,
	SimpleChanges,
	ChangeDetectorRef,
} from '@angular/core';
import { forkJoin } from 'rxjs';
import { CodeDecodeValue } from '../../models/common/code-decode-value';
import { TimeZoneValue } from '../../models/common/time-zone-value';
import { Company } from '../../models/companies/company';
import { CompanyLegalAcceptanceRequest } from '../../models/companies/company-legal-acceptance-request';
import { PublicCompany } from '../../models/companies/public-company';
import { CompanyLegalAcceptance } from '../../models/funding-request/company-legal-acceptance';
import { InvitationToken } from '../../models/invitations/token';
import { OperationError } from '../../models/server-result/operation-error';
import { OperationResult } from '../../models/server-result/operation-result';
import { RegisterUserInput } from '../../models/User/register-user-input';
import { ValidationRule } from '../../models/validation-rules/validation-rules';
import { ServerApiService } from '../../server-api/server-api.service';
import { AppContextService } from '../../services/app-context.service';
import { ToasterService } from '../../shared-components/custom-toaster/toaster.service';
import { TranslateService } from '../../services/translate/translate.service';
import { UtilitiesService } from '../../services/utils/utilities.service';
import { ComponentType, FormValidationUtils } from '../../utils/form-validation';
import { SmartFormBaseComponent } from '../../utils/smart-form-base.component';
import { CustomCheckboxComponent } from '../custom-checkbox/custom-checkbox.component';
import { CustomInputComponent } from '../custom-input/custom-input.component';
import { SelectOption, CustomSelectComponent } from '../custom-select/custom-select.component';
import { html } from '../../utils/html-tag-utils';
import { TableColumn } from '../custom-table/custom-table.types';
import { UserApiService } from '../../server-api/user-api/user-api.service';
import { PersonalDataConsentLinkAcceptance, PersonalDataConsentLinkRequest } from '../../server-api/user-api/user-api.models';

@Component({
	selector: 'eif-invitation-screen',
	templateUrl: './invitation-screen.component.html',
	styleUrls: ['./invitation-screen.component.scss'],
	standalone: false,
})
export class InvitationScreenComponent extends SmartFormBaseComponent implements OnInit, AfterViewInit, OnChanges {
	@Input() showSucessSubmission: boolean;
	@Input() sucessSubmissionContent: string;
	@Input() token: InvitationToken;
	@Input() invitedCompany: Company;
	@Input() invitingCompany: PublicCompany;
	@Input() invitedUser: { Email: string; Name: string };
	@Input() invitedUserEmail: string;
	@Input() newUser: boolean;
	@Input() invitationScreenType: null | 'supplierFactoring' | 'buyerConfirming' | 'supplierConfirming' | 'user' = null;
	@Input() registerUserInput: RegisterUserInput = new RegisterUserInput();
	@Input() requestErrors: OperationError[] = [];
	@Input() languageCodes: CodeDecodeValue[];
	@Input() timeZones: TimeZoneValue[];
	@Input() pageDisplayLanguageCode: string | null = null;
	@Input() contractData: any | null = null; // For supplierConfirming - PublicContract data
	@Input() bankAccountData: { iban: string; swift: string } | null = null; // For supplierConfirming - Bank details
	@Input() isSubmittingAccept: boolean = false; // Loading state for accept submission
	@Input() isSubmittingRefuse: boolean = false; // Loading state for refuse submission

	@Output() acceptInvitation = new EventEmitter<{
		registerUserInput: RegisterUserInput;
		invitedCompany: Company | null;
	}>();
	@Output() refuseInvitation = new EventEmitter<{ registerUserInput: RegisterUserInput }>();
	@Output() documentsUpdated = new EventEmitter<{
		legalAcceptance: CompanyLegalAcceptance;
		personalDataConsent: PersonalDataConsentLinkAcceptance;
	}>();
	@Output() pageDisplayLanguageCodeChange = new EventEmitter<string>();

	// Internal properties for translations
	public invitedUserIdentification: string | null = null;
	public invitationCompaniesIdentification: string | null = null;
	public invitationTermsAndConditionsAcceptance: string | null = null;
	public textTolinkWithContract: string | null = null;
	public viewContract: string | null = null;
	public textTolinkWithPersonalDataConsentDocument: string | null = null;
	public viewPersonalDataConsentDocument: string | null = null;
	public textTolinkWithPersonalDataConsentDocumentPart2: string | null = null;
	public personalDataConsentDocumentName: string | null = null;
	public termsConditionsDocumentName: string | null = null;

	// Internal document blobs
	public contractBlob: Blob;
	public personalDataConsentLink: string;

	// Loading state for document fetching
	public isFetchingDocuments: boolean = false;
	private hasInitiallyFetchedDocuments: boolean = false;

	// Flag to prevent form reset during submission
	private isSubmitting: boolean = false;

	protected _languageCodeOptions: SelectOption[] = [];
	protected _timeZoneOptions: SelectOption[] = [];
	protected _userLanguageCodeOptions: SelectOption[] = [];
	protected _userTimeZoneOptions: SelectOption[] = [];

	// Form management
	@ViewChildren(CustomInputComponent) inputComponents!: QueryList<CustomInputComponent>;
	@ViewChildren(CustomSelectComponent) selectComponents!: QueryList<CustomSelectComponent>;
	@ViewChildren(CustomCheckboxComponent) checkboxComponents!: QueryList<CustomCheckboxComponent>;

	// Password values for validation
	public passwordValue: string | null = null;
	public repeatPasswordValue: string | null = null;

	// Contract table configuration (for supplierConfirming)
	public contractTableColumns: TableColumn[] = [];
	public contractTableRows: any[] = [];

	constructor(
		private translateService: TranslateService,
		public utilitiesService: UtilitiesService,
		private serverApiService: ServerApiService,
		private userApiService: UserApiService,
		private toasterService: ToasterService,
		private appContext: AppContextService,
		private cdr: ChangeDetectorRef
	) {
		super();
	}

	// Field configurations with validation rules
	// NOTE: Order must match the template component order by type (inputs, selects, checkboxes)!
	private getCompanyFieldConfigs() {
		const inputRules = CustomInputComponent.createRules(this.translateService, this.pageDisplayLanguageCode || undefined);
		const selectRules = CustomSelectComponent.createRules(this.translateService, this.pageDisplayLanguageCode || undefined);

		return [
			{ name: 'countryCode', type: ComponentType.INPUT },
			{ name: 'vatNumber', type: ComponentType.INPUT },
			{ name: 'companyName', type: ComponentType.INPUT, validationRules: [inputRules.required()] },
			{ name: 'companyAddressLine1', type: ComponentType.INPUT, validationRules: [inputRules.required()] },
			{ name: 'companyAddressLine2', type: ComponentType.INPUT },
			{ name: 'companyAddressLine3', type: ComponentType.INPUT },
			{ name: 'companyZipCode', type: ComponentType.INPUT, validationRules: [inputRules.required()] },
			{ name: 'companyCity', type: ComponentType.INPUT, validationRules: [inputRules.required()] },
			{ name: 'companyShareCapital', type: ComponentType.INPUT, validationRules: this.shareCapitalValidationRules },
			{ name: 'companyTimeZone', type: ComponentType.SELECT, validationRules: [selectRules.required()] },
			{ name: 'companyLanguageCode', type: ComponentType.SELECT, validationRules: [selectRules.required()] },
		];
	}

	private getUserFieldConfigs() {
		const inputRules = CustomInputComponent.createRules(this.translateService, this.pageDisplayLanguageCode || undefined);
		const selectRules = CustomSelectComponent.createRules(this.translateService, this.pageDisplayLanguageCode || undefined);

		return [
			{ name: 'username', type: ComponentType.INPUT },
			{ name: 'email', type: ComponentType.INPUT },
			{ name: 'userName', type: ComponentType.INPUT, validationRules: [inputRules.required()] },
			{ name: 'userTelephone', type: ComponentType.INPUT, validationRules: [inputRules.required()] },
			{ name: 'userPassword', type: ComponentType.INPUT },
			{ name: 'userRepeatPassword', type: ComponentType.INPUT },
			{ name: 'userLanguageCode', type: ComponentType.SELECT, validationRules: [selectRules.required()] },
			{ name: 'userTimeZone', type: ComponentType.SELECT, validationRules: [selectRules.required()] },
		];
	}

	private getCheckboxFieldConfigs() {
		const checkboxRules = CustomCheckboxComponent.createRules(this.translateService, this.pageDisplayLanguageCode || undefined);

		return [
			{ name: 'contractAccepted', type: ComponentType.CHECKBOX, validationRules: [checkboxRules.required()] },
			{ name: 'personalDataAccepted', type: ComponentType.CHECKBOX, validationRules: [checkboxRules.required()] },
		];
	}

	private get inputRules() {
		return CustomInputComponent.createRules(this.translateService, this.pageDisplayLanguageCode || undefined);
	}

	/**
	 * Recreates the form manager with the current pageDisplayLanguageCode
	 * This ensures all validation messages use the correct language
	 */
	private recreateFormManagerWithCurrentLanguage(): void {
		// Preserve current checkbox values before recreating form manager
		const preservedContractAccepted = this.formFields.contractAccepted?.value ?? false;
		const preservedPersonalDataAccepted = this.formFields.personalDataAccepted?.value ?? false;

		const allFieldConfigs = [...this.getCompanyFieldConfigs(), ...this.getUserFieldConfigs(), ...this.getCheckboxFieldConfigs()];
		this.formManager = FormValidationUtils.createSmartFormManagerWithQueryList(
			allFieldConfigs,
			this.translateService,
			this.inputComponents,
			this.selectComponents,
			this.checkboxComponents,
			undefined, // switchComponents - not used in invitation screen
			undefined, // radioComponents - not used in invitation screen
			undefined, // datePickerComponents - not used in invitation screen
			undefined, // sliderComponents
			this.pageDisplayLanguageCode || undefined
		);

		// Set password validation rules explicitly after component mapping
		if (this.formManager?.formFields) {
			this.formManager.formFields.userPassword.validationRules = this.passwordValidationRules;
			this.formManager.formFields.userRepeatPassword.validationRules = this.repeatPasswordValidationRules;

			// Restore preserved checkbox values after form manager recreation
			// Update both formFields AND the actual checkbox components
			this.formManager.formFields.contractAccepted.value = preservedContractAccepted;
			this.formManager.formFields.contractAccepted.valid = preservedContractAccepted;
			if (this.formManager.formFields.contractAccepted.component) {
				(this.formManager.formFields.contractAccepted.component as CustomCheckboxComponent).writeValue(preservedContractAccepted);
			}

			this.formManager.formFields.personalDataAccepted.value = preservedPersonalDataAccepted;
			this.formManager.formFields.personalDataAccepted.valid = preservedPersonalDataAccepted;
			if (this.formManager.formFields.personalDataAccepted.component) {
				(this.formManager.formFields.personalDataAccepted.component as CustomCheckboxComponent).writeValue(preservedPersonalDataAccepted);
			}
		}
	}

	ngAfterViewInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		// Re-initialize with actual component references
		const allFieldConfigs = [...this.getCompanyFieldConfigs(), ...this.getUserFieldConfigs(), ...this.getCheckboxFieldConfigs()];
		this.formManager = FormValidationUtils.createSmartFormManagerWithQueryList(
			allFieldConfigs,
			this.translateService,
			this.inputComponents,
			this.selectComponents,
			this.checkboxComponents,
			undefined, // switchComponents - not used in invitation screen
			undefined, // radioComponents - not used in invitation screen
			undefined, // datePickerComponents - not used in invitation screen
			undefined, // sliderComponents
			this.pageDisplayLanguageCode || undefined
		);

		// Set password validation rules explicitly after component mapping
		if (this.formManager?.formFields) {
			this.formManager.formFields.userPassword.validationRules = this.passwordValidationRules;
			this.formManager.formFields.userRepeatPassword.validationRules = this.repeatPasswordValidationRules;
		}
	}

	// Checkbox getters for template
	get isContractAccepted(): boolean {
		return this.formFields.contractAccepted?.value || false;
	}

	set isContractAccepted(value: boolean) {
		if (this.formFields.contractAccepted) {
			this.formFields.contractAccepted.value = value;
		}
	}

	get isPersonalDataConsentDocumentAccepted(): boolean {
		return this.formFields.personalDataAccepted?.value || false;
	}

	set isPersonalDataConsentDocumentAccepted(value: boolean) {
		if (this.formFields.personalDataAccepted) {
			this.formFields.personalDataAccepted.value = value;
		}
	}

	ngOnInit() {
		// Initialize form and translations if data is available
		if (this.pageDisplayLanguageCode) {
			this.recreateFormManagerWithCurrentLanguage();
			this.updateAllTranslations();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Update options when input data changes
		if (changes['languageCodes'] || changes['invitedCompany']) {
			this.updateLanguageCodeOptions();
		}
		if (changes['timeZones'] || changes['invitedCompany']) {
			this.updateTimeZoneOptions();
		}
		if (changes['languageCodes'] || changes['registerUserInput']) {
			this.updateUserLanguageCodeOptions();
		}
		if (changes['timeZones'] || changes['registerUserInput']) {
			this.updateUserTimeZoneOptions();
		}
		if (changes['pageDisplayLanguageCode'] && !changes['pageDisplayLanguageCode'].firstChange) {
			// Only recreate form manager if we're not in the middle of submitting
			// This prevents form reset when updating language during submission
			if (!this.isSubmitting) {
				this.recreateFormManagerWithCurrentLanguage();
			}
			this.updateAllTranslations();
			// Update contract table translations if contract data exists
			if (this.contractData && this.invitationScreenType === 'supplierConfirming') {
				this.initializeContractTable();
			}
		}
		// Update translations when company/user data arrives
		if (changes['invitedCompany'] || changes['invitingCompany'] || changes['invitedUser']) {
			this.updateAllTranslations();
		}

		// Initialize contract table when contract data arrives or changes
		if (changes['contractData'] || changes['bankAccountData']) {
			if (this.contractData && this.invitationScreenType === 'supplierConfirming') {
				this.initializeContractTable();
			}
		}

		// Fetch initial documents when we have the required data
		// Use user language code to keep acceptance language aligned with payload
		if (!this.hasInitiallyFetchedDocuments && this.invitedUserEmail && this.registerUserInput?.LanguageCode) {
			this.fetchDocuments(this.registerUserInput.LanguageCode);
		}

		// Reset submission flag when success screen is shown
		if (changes['showSucessSubmission'] && this.showSucessSubmission) {
			this.isSubmitting = false;
		}
	}

	public openContract(): void {
		this.utilitiesService.openBlobNewWindowWithName(this.contractBlob, this.termsConditionsDocumentName!, false);
	}

	public openPersonalDataConsentDocument(): void {
		window.open(this.personalDataConsentLink, '_blank');
	}

	public get isAcceptDisabled(): boolean {
		return this.isSubmittingAccept || this.isSubmittingRefuse || this.isFetchingDocuments || !this.hasInitiallyFetchedDocuments;
	}

	public onRefuseInvitation(): void {
		// Set flag to prevent form reset during submission
		this.isSubmitting = true;

		// Use the original logic to update language and translations
		this.translateMessagesWithUserLanguageCode(false);

		// Trigger validation on all fields to show any errors before refusing
		if (this.formManager) {
			this.formManager.validateAll();
			this.updateFormValidity();
			// Trigger change detection to show validation errors
			this.cdr.detectChanges();
		}

		this.refuseInvitation.emit({
			registerUserInput: this.registerUserInput,
		});
	}

	public onAcceptInvitation(): void {
		if (this.isFetchingDocuments || !this.hasInitiallyFetchedDocuments) {
			return;
		}

		// Set flag to prevent form reset during submission
		this.isSubmitting = true;

		// Sync values from model objects to form manager before validation
		this.syncFormValuesFromModels();

		// Use setTimeout to ensure Angular has synced all ngModel values
		setTimeout(() => {
			// Trigger validation on all fields first (before language change)
			if (this.formManager) {
				this.formManager.validateAll();
				this.updateFormValidity();
				// Trigger change detection to show validation errors
				this.cdr.detectChanges();
			}

			// Check validation BEFORE updating language/translations
			// Use formManager.isValid() pattern from profile-form-example
			const isValid = this.formIsValid();
			if (!isValid) {
				// Reset flag if validation failed
				this.isSubmitting = false;
				return;
			}

			// Only update language and translations if validation passed
			// Use the original logic to update language and translations
			this.translateMessagesWithUserLanguageCode(false);

			if (this.newUser) {
				this.registerUserInput.Username = this.registerUserInput.Email;
			}

			// For user type, only emit registerUserInput (no company)
			this.acceptInvitation.emit({
				registerUserInput: this.registerUserInput,
				invitedCompany: this.invitationScreenType === 'user' ? null : this.invitedCompany,
			});
		}, 0);
	}

	/**
	 * Syncs values from model objects (invitedCompany, registerUserInput) to form manager fields
	 * This ensures validation checks the actual form values
	 * Uses formManager.setValues() like profile-form-example for proper syncing
	 */
	private syncFormValuesFromModels(): void {
		if (!this.formManager || !this.formFields) return;

		const valuesToSet: { [key: string]: any } = {};

		// Sync company fields (only if not user invitation type)
		if (this.invitationScreenType !== 'user' && this.invitedCompany) {
			valuesToSet.companyName = this.invitedCompany.Name || '';
			valuesToSet.companyAddressLine1 = this.invitedCompany.AddressLine1 || '';
			valuesToSet.companyAddressLine2 = this.invitedCompany.AddressLine2 || '';
			valuesToSet.companyAddressLine3 = this.invitedCompany.AddressLine3 || '';
			valuesToSet.companyZipCode = this.invitedCompany.ZipCode || '';
			valuesToSet.companyCity = this.invitedCompany.City || '';
			valuesToSet.companyShareCapital = this.invitedCompany.ShareCapital?.toString() || '';
			valuesToSet.companyTimeZone = this.invitedCompany.TimeZone || '';
			valuesToSet.companyLanguageCode = this.invitedCompany.LanguageCode || '';
		}

		// Sync user fields (only if newUser)
		if (this.newUser && this.registerUserInput) {
			valuesToSet.userName = this.registerUserInput.Name || '';
			valuesToSet.userTelephone = this.registerUserInput.Telephone || '';
			valuesToSet.userPassword = this.registerUserInput.Pawd || '';
			valuesToSet.userRepeatPassword = this.registerUserInput.RepeatePawd || '';
			valuesToSet.userLanguageCode = this.registerUserInput.LanguageCode || '';
			valuesToSet.userTimeZone = this.registerUserInput.TimeZone || '';
			// Update passwordValue for repeat password validation
			this.passwordValue = this.registerUserInput.Pawd || null;
			this.repeatPasswordValue = this.registerUserInput.RepeatePawd || null;
		}

		// Sync checkbox values
		valuesToSet.contractAccepted = this.isContractAccepted;
		valuesToSet.personalDataAccepted = this.isPersonalDataConsentDocumentAccepted;

		// Use formManager.setValues() to sync all values at once (like profile-form-example)
		if (Object.keys(valuesToSet).length > 0) {
			this.formManager.setValues(valuesToSet);
		}
	}

	private formIsValid(): boolean {
		if (!this.formManager) return false;

		const fieldsToValidate = this.getFieldsToValidate();

		return fieldsToValidate.every((fieldName) => {
			const field = this.formFields[fieldName];
			if (!field) return false;

			// For checkboxes, check both valid and value === true
			const checkboxConfigs = this.getCheckboxFieldConfigs();
			const isCheckbox = checkboxConfigs.some((config) => config.name === fieldName);
			if (isCheckbox) {
				return field.valid && field.value === true;
			}

			// If field has no component reference but has a value, consider it valid
			// This handles cases where components are conditionally rendered (hidden/ngIf)
			// and the form manager couldn't map them, but the model has the value
			if (!field.component && field.value) {
				return true;
			}

			return field.valid;
		});
	}

	/**
	 * Returns list of field names that should be validated based on current conditions
	 * Similar to how missing-accepted component checks allDocsAccepted
	 */
	private getFieldsToValidate(): string[] {
		const fieldsToValidate: string[] = [];

		// Company fields (only if not user invitation type)
		if (this.invitationScreenType !== 'user') {
			this.getCompanyFieldConfigs().forEach((config) => {
				// Skip read-only and optional fields
				if (
					config.name !== 'countryCode' &&
					config.name !== 'vatNumber' &&
					config.name !== 'companyAddressLine2' &&
					config.name !== 'companyAddressLine3'
				) {
					fieldsToValidate.push(config.name);
				}
			});
		}

		// User fields (only if newUser)
		if (this.newUser) {
			this.getUserFieldConfigs().forEach((config) => {
				// Skip read-only fields
				if (config.name !== 'username' && config.name !== 'email') {
					fieldsToValidate.push(config.name);
				}
			});
		}

		// Checkboxes (always required) - like missing-accepted component checks all checkboxes
		this.getCheckboxFieldConfigs().forEach((config) => {
			fieldsToValidate.push(config.name);
		});

		return fieldsToValidate;
	}

	public validateOnlyNumbers(e) {
		return this.utilitiesService.validateOnlyNumbers(e);
	}

	public translateMessagesWithUserLanguageCode(shouldFetchDocuments: boolean = true): void {
		// Update internal language code
		this.pageDisplayLanguageCode = this.registerUserInput.LanguageCode;

		// Emit the language change to parent components
		this.pageDisplayLanguageCodeChange.emit(this.registerUserInput.LanguageCode);

		// Update application language in localStorage so translate pipe picks it up
		this.appContext.setApplicationLanguageCode(this.registerUserInput.LanguageCode);

		// Only recreate form manager if we're not in the middle of submitting
		// This prevents form reset when updating language during submission
		if (!this.isSubmitting) {
			this.recreateFormManagerWithCurrentLanguage();
		}

		// Update error messages for fields that are already showing errors
		// without triggering validation on untouched fields
		setTimeout(() => {
			if (this.formManager?.refreshValidationMessages) {
				this.formManager.refreshValidationMessages();
			}
		}, 0);

		this.updateLanguageCodeOptions();
		this.updateTimeZoneOptions();
		this.updateUserLanguageCodeOptions();
		this.updateUserTimeZoneOptions();

		// Update all translations with new language
		this.updateAllTranslations();

		// Fetch documents in new language (if requested)
		if (shouldFetchDocuments) {
			this.fetchDocuments(this.registerUserInput.LanguageCode);
		}
	}

	private updateAllTranslations(): void {
		if (!this.pageDisplayLanguageCode) return;

		// Handle different invitation types
		if (this.invitationScreenType === 'user') {
			// User invitation type - simpler contextualization
			if (this.invitedUserEmail) {
				this.invitedUserIdentification = this.translateService
					.translateByLanguage('user-invitation-response.ContextualizationInvitedUser', this.pageDisplayLanguageCode, 'client')
					.replace('[Email]', this.invitedUserEmail);
			}

			if (this.invitingCompany?.Name) {
				this.invitationCompaniesIdentification = this.translateService
					.translateByLanguage('user-invitation-response.ContextualizationInvitedCompany', this.pageDisplayLanguageCode, 'client')
					.replace('[CompanyName]', this.invitingCompany.Name);
			}
		} else {
			// Company invitation types (supplier, buyer, etc.)
			if (this.invitedUser?.Name) {
				this.invitedUserIdentification = this.translateService
					.translateByLanguage('company-relation-invitation-response.invitedUserIdentification', this.pageDisplayLanguageCode, 'client')
					.replace('[UserName]', this.invitedUser.Name);
			}

			if (this.invitedCompany && this.invitingCompany) {
				this.invitationCompaniesIdentification = this.translateService
					.translateByLanguage(
						'company-relation-invitation-response.invitationCompaniesIdentification',
						this.pageDisplayLanguageCode,
						'client'
					)
					.replace('[InvitedCompanyName]', this.invitedCompany.Name || '')
					.replace('[InvitedCompanyContryCode]', this.invitedCompany.CountryCode || '')
					.replace('[InvitedCompanyVatNumber]', this.invitedCompany.VatNumber || '')
					.replace('[InvitingCompanyName]', this.invitingCompany.Name || '')
					.replace('[InvitingCompanyContryCode]', this.invitingCompany.CountryCode || '')
					.replace('[InvitingCompanyVatNumber]', this.invitingCompany.VatNumber || '');

				this.invitationTermsAndConditionsAcceptance = this.translateService
					.translateByLanguage(
						'company-relation-invitation-response.invitationTermsAndConditionsAcceptance',
						this.pageDisplayLanguageCode,
						'client'
					)
					.replace('[InvitedCompanyName]', this.invitedCompany.Name || '')
					.replace('[InvitedCompanyContryCode]', this.invitedCompany.CountryCode || '')
					.replace('[InvitedCompanyVatNumber]', this.invitedCompany.VatNumber || '')
					.replace('[InvitingCompanyName]', this.invitingCompany.Name || '')
					.replace('[InvitingCompanyContryCode]', this.invitingCompany.CountryCode || '')
					.replace('[InvitingCompanyVatNumber]', this.invitingCompany.VatNumber || '');
			}
		}

		// Contract and legal document texts
		// Use different translation keys for user invitations vs company invitations
		const translationPrefix =
			this.invitationScreenType === 'user' ? 'user-invitation-response-modal' : 'company-relation-invitation-response-modal';

		this.textTolinkWithContract = this.translateService.translateByLanguage(
			`${translationPrefix}.textTolinkWithContract`,
			this.pageDisplayLanguageCode,
			'client'
		);
		this.viewContract = this.translateService.translateByLanguage(
			`${translationPrefix}.viewContract`,
			this.pageDisplayLanguageCode,
			'client'
		);

		this.textTolinkWithPersonalDataConsentDocument = this.translateService.translateByLanguage(
			`${translationPrefix}.textTolinkWithPersonalDataConsentDocument`,
			this.pageDisplayLanguageCode,
			'client'
		);
		this.viewPersonalDataConsentDocument = this.translateService.translateByLanguage(
			`${translationPrefix}.viewPersonalDataConsentDocument`,
			this.pageDisplayLanguageCode,
			'client'
		);
		this.textTolinkWithPersonalDataConsentDocumentPart2 = this.translateService.translateByLanguage(
			`${translationPrefix}.textTolinkWithPersonalDataConsentDocumentPart2`,
			this.pageDisplayLanguageCode,
			'client'
		);

		this.personalDataConsentDocumentName = this.translateService.translateByLanguage(
			'saphety_personnal_data_doc_name',
			this.pageDisplayLanguageCode,
			'list-DownloadDocNames'
		);

		this.termsConditionsDocumentName = this.translateService.translateByLanguage(
			'saphety_terms_conditions_doc_name',
			this.pageDisplayLanguageCode,
			'list-DownloadDocNames'
		);
	}

	protected get languageCodeOptions(): SelectOption[] {
		return this._languageCodeOptions;
	}

	private updateLanguageCodeOptions(): void {
		this._languageCodeOptions =
			this.languageCodes?.map((languageCode) => ({
				label: `${languageCode.Code} - ${languageCode.Decode}`,
				value: languageCode.Code,
				selected: languageCode.Code === this.invitedCompany?.LanguageCode,
			})) || [];
	}

	protected onLanguageCodeSelectionChange(selectedValue: any): void {
		if (this.invitedCompany) {
			this.invitedCompany.LanguageCode = selectedValue || '';
			this.updateLanguageCodeOptions();
		}
	}

	protected get timeZoneOptions(): SelectOption[] {
		return this._timeZoneOptions;
	}

	private updateTimeZoneOptions(): void {
		this._timeZoneOptions =
			this.timeZones?.map((timeZone) => ({
				label: this.translateService.translate(timeZone.TimeZoneName, 'list-timeZones'),
				value: timeZone.IanaCode,
				selected: timeZone.IanaCode === this.invitedCompany?.TimeZone,
			})) || [];
	}

	protected onTimeZoneSelectionChange(selectedValue: any): void {
		if (this.invitedCompany) {
			this.invitedCompany.TimeZone = selectedValue || '';
			this.updateTimeZoneOptions();
		}
	}

	protected get userLanguageCodeOptions(): SelectOption[] {
		return this._userLanguageCodeOptions;
	}

	private updateUserLanguageCodeOptions(): void {
		this._userLanguageCodeOptions =
			this.languageCodes?.map((languageCode) => ({
				label: `${languageCode.Code} - ${languageCode.Decode}`,
				value: languageCode.Code,
				selected: languageCode.Code === this.registerUserInput?.LanguageCode,
			})) || [];
	}

	protected onUserLanguageCodeSelectionChange(selectedValue: any): void {
		if (this.registerUserInput) {
			this.registerUserInput.LanguageCode = selectedValue || '';
			this.updateUserLanguageCodeOptions();
			this.translateMessagesWithUserLanguageCode();
		}
	}

	protected get userTimeZoneOptions(): SelectOption[] {
		return this._userTimeZoneOptions;
	}

	private updateUserTimeZoneOptions(): void {
		this._userTimeZoneOptions =
			this.timeZones?.map((timeZone) => ({
				label: this.translateService.translate(timeZone.TimeZoneName, 'list-timeZones'),
				value: timeZone.IanaCode,
				selected: timeZone.IanaCode === this.registerUserInput?.TimeZone,
			})) || [];
	}

	protected onUserTimeZoneSelectionChange(selectedValue: any): void {
		if (this.registerUserInput) {
			this.registerUserInput.TimeZone = selectedValue || '';
			this.updateUserTimeZoneOptions();
		}
	}

	// Event handlers for form fields
	override onValidationChange(fieldName: string, validation: any): void {
		if (this.formManager?.formFields?.[fieldName]) {
			this.formManager.formFields[fieldName].valid = validation.isValid;
			// Use the same validation logic as profile-form-example and missing-accepted
			// Update form validity like profile-form-example does
			this.updateFormValidity();
		}
	}

	/**
	 * Updates form validity state - similar to missing-accepted component's allDocumentsAccepted()
	 * Uses formManager.isValid() pattern from profile-form-example
	 */
	private updateFormValidity(): void {
		if (!this.formManager) {
			this.isFormValid = false;
			return;
		}

		// Check only fields that should be validated based on conditions
		const fieldsToValidate = this.getFieldsToValidate();
		const checkboxConfigs = this.getCheckboxFieldConfigs();
		this.isFormValid = fieldsToValidate.every((fieldName) => {
			const field = this.formFields[fieldName];
			if (!field) return false;

			// For checkboxes, check both valid and value === true
			const isCheckbox = checkboxConfigs.some((config) => config.name === fieldName);
			if (isCheckbox) {
				return field.valid && field.value === true;
			}

			return field.valid;
		});
	}

	// Password change handlers
	onPasswordChange(value: string): void {
		this.passwordValue = value;
		this.onValueChange('userPassword', value);
	}

	onRepeatPasswordChange(value: string): void {
		this.repeatPasswordValue = value;
		this.onValueChange('userRepeatPassword', value);
	}

	// Password validation rules
	get passwordValidationRules(): ValidationRule[] {
		return [
			this.inputRules.minLength(8),
			this.inputRules.custom(
				(value) => /\d/.test(value),
				this.translateService.translateByLanguage('resetPassword.passwordRule2', this.pageDisplayLanguageCode!, 'client'),
				'has-digit'
			),
			this.inputRules.custom(
				(value) => /[A-Z]/.test(value),
				this.translateService.translateByLanguage('resetPassword.passwordRule3', this.pageDisplayLanguageCode!, 'client'),
				'has-uppercase'
			),
			this.inputRules.custom(
				(value) => /[a-z]/.test(value),
				this.translateService.translateByLanguage('resetPassword.passwordRule4', this.pageDisplayLanguageCode!, 'client'),
				'has-lowercase'
			),
			this.inputRules.custom(
				(value) => /[!@#$%^&*?_~-£()]/.test(value),
				this.translateService.translateByLanguage('resetPassword.passwordRule5', this.pageDisplayLanguageCode!, 'client'),
				'has-special-char'
			),
		];
	}

	get repeatPasswordValidationRules(): ValidationRule[] {
		return [
			this.inputRules.custom(
				(value) => value === this.passwordValue,
				this.translateService.translateByLanguage('field.passwordsDoNotMatch', this.pageDisplayLanguageCode!, 'client'),
				'passwords-do-not-match'
			),
		];
	}

	get shareCapitalValidationRules(): ValidationRule[] {
		return [
			this.inputRules.required(),
			this.inputRules.custom(
				(value: string) => {
					if (!value || value.trim() === '') return true;
					const numericValue = parseFloat(value.replace(/[^\d.-]/g, ''));
					return !isNaN(numericValue) && numericValue > 0;
				},
				this.translateService
					.translateByLanguage('input.validation.greaterThan', this.pageDisplayLanguageCode!, 'client')
					.replace('[VALUE]', '0'),
				'share-capital-greater-than-zero'
			),
		];
	}

	/**
	 * Fetch legal documents in the specified language
	 * Called on initial load and when user changes language
	 * Emits documentsUpdated event to notify parent of new document hashes
	 */
	private fetchDocuments(languageCodeToUse: string): void {
		if (!this.invitedUserEmail) return;

		const legalUserAcceptanceInputDto: CompanyLegalAcceptanceRequest = {
			Username: this.invitedUserEmail,
			LanguageCode: languageCodeToUse,
		};

		const personalDataConsentLinkServiceCallInputDto: PersonalDataConsentLinkRequest = {
			Username: this.invitedUserEmail,
			LanguageCode: languageCodeToUse,
		};

		const createUserLegalAcceptanceServiceCall = this.serverApiService.createCompanyLegalAcceptance(legalUserAcceptanceInputDto);
		const createPersonalDataConsentDocumentServiceCall = this.userApiService.getPersonalDataConsentLink(
			personalDataConsentLinkServiceCallInputDto
		);

		this.isFetchingDocuments = true;

		forkJoin([createUserLegalAcceptanceServiceCall, createPersonalDataConsentDocumentServiceCall]).subscribe({
			next: (opResults) => {
				this.isFetchingDocuments = false;
				this.hasInitiallyFetchedDocuments = true;

				const createUserLegalAcceptanceResult = opResults[0] as OperationResult<CompanyLegalAcceptance>;
				const createPersonalDataConsentLinkResult = opResults[1] as OperationResult<PersonalDataConsentLinkAcceptance>;

				if (!createUserLegalAcceptanceResult.IsValid) {
					createUserLegalAcceptanceResult.Errors?.forEach((e) =>
						this.toasterService.showError(
							this.translateService.translateByLanguage(e.Code, this.pageDisplayLanguageCode!, 'list-errorCodes')
						)
					);
				}

				if (!createPersonalDataConsentLinkResult.IsValid) {
					createPersonalDataConsentLinkResult.Errors?.forEach((e) =>
						this.toasterService.showError(
							this.translateService.translateByLanguage(e.Code, this.pageDisplayLanguageCode!, 'list-errorCodes')
						)
					);
				}

				if (createUserLegalAcceptanceResult.IsValid && createPersonalDataConsentLinkResult.IsValid) {
					const legalUserAcceptance = createUserLegalAcceptanceResult.Data;
					this.contractBlob = this.utilitiesService.base64ToBlob(legalUserAcceptance.Document, legalUserAcceptance.ContentType);

					const personalDataConsentDocumentAcceptance = createPersonalDataConsentLinkResult.Data;
					this.personalDataConsentLink = createPersonalDataConsentLinkResult.Data.DocumentLink;

					// Emit document updates to parent so they can update their hashes
					// This happens on initial load AND when language changes
					this.documentsUpdated.emit({
						legalAcceptance: legalUserAcceptance,
						personalDataConsent: personalDataConsentDocumentAcceptance,
					});
				}
			},
			error: (httpErrorResponse) => {
				this.isFetchingDocuments = false;
				console.error('error: ' + httpErrorResponse.message);
				this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
			},
		});
	}

	/**
	 * Initialize contract table columns and rows for supplierConfirming invitation type
	 */
	private initializeContractTable(): void {
		if (!this.contractData || !this.pageDisplayLanguageCode) return;

		// Define table columns - headers are translation keys (table applies translate pipe)
		this.contractTableColumns = [
			{
				field: 'externalContractId',
				header: 'invitation-screen.contract',
				align: 'left',
				flex: 0.5,
				render: (row: any) => {
					return html`
						<p class="is-bold">${row.externalContractId}</p>
						<span>${this.translateService.translate('invitation-screen.confirming')}</span>
					`;
				},
				tooltip: (row: any) => `${row.externalContractId} ${this.translateService.translate('invitation-screen.confirming')}`,
			},
			{
				field: 'currency',
				header: 'invitation-screen.currency',
				align: 'right',
				flex: 0.5,
				render: (row: any) => {
					return html`<span class="text-uppercase is-bold">${row.currency}</span>`;
				},
			},
			{
				field: 'financierName',
				header: 'invitation-screen.financier',
				align: 'left',
				render: (row: any) => {
					return html`
						<p class="is-bold">${row.financierName}</p>
						<span>${row.financierCountry}${row.financierVat}</span>
					`;
				},
				tooltip: (row: any) => `${row.financierName} ${row.financierCountry}${row.financierVat}`,
			},
			{
				field: 'iban',
				header: 'invitation-screen.iban',
				align: 'left',
				render: (row: any) => {
					return html`<div>${row.iban}</div>`;
				},
			},
			{
				field: 'swift',
				header: 'invitation-screen.swift',
				align: 'left',
				render: (row: any) => {
					return html`<span>${row.swift}</span>`;
				},
			},
		];

		// Define table rows - create new array to trigger change detection
		this.contractTableRows = [
			...[
				{
					id: 1,
					externalContractId: this.contractData.ExternalContractId || '',
					currency: this.contractData.Currency || '',
					financierName: this.contractData.Financier?.Name || '',
					financierCountry: this.contractData.Financier?.CountryCode || '',
					financierVat: this.contractData.Financier?.VatNumber || '',
					iban: this.bankAccountData?.iban || '',
					swift: this.bankAccountData?.swift || '',
				},
			],
		];
	}
}
